import React, { useState, useEffect, useMemo } from 'react';
import { getCustomers, updateCustomer, getCustomerMessages } from '../services/apiServices'; // Importa getCustomers, updateCustomer e getCustomerMessages
import styles from './Clients.module.css'; // Corrigido para manter consistência
import { FaEdit, FaSave, FaTimes, FaUser, FaPhone, FaMapMarkerAlt, FaShoppingCart, FaClock, FaChartLine, FaSearch, FaFilter, FaComment } from 'react-icons/fa'; // Importa ícones

const Clients = () => {
  const [enrichedClients, setEnrichedClients] = useState([]); // Estado para dados combinados
  const [filteredClients, setFilteredClients] = useState([]); // Estado para a tabela
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('date_desc'); // Padrão para 'Mais Recentes'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingClient, setEditingClient] = useState(null); // Guarda o cliente sendo editado

  // Estados para o Modal de Histórico de Conversa
  const [selectedClientForChat, setSelectedClientForChat] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        // Busca apenas os clientes (que já possuem a contagem de pedidos no documento - otimização de alta performance)
        const clientsData = await getCustomers(true);

        const clientsWithOrderCount = clientsData.map(client => ({
          ...client,
          orderCount: Number(client.orderCount) || 0
        }));

        setEnrichedClients(clientsWithOrderCount);
      } catch (e) {
        setError('Falha ao carregar os clientes.');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);
  
  useEffect(() => {
    let results = enrichedClients.filter(client => {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const addressString = client.address ? `${client.address.streetAndNumber || ''} ${client.address.neighborhood || ''}`.toLowerCase() : '';
      
      return (
        (client.name && client.name.toLowerCase().includes(lowerSearchTerm)) ||
        client.id.includes(searchTerm) ||
        addressString.includes(lowerSearchTerm)
      );
    });

    // Aplica a ordenação
    results.sort((a, b) => {
      if (sortOrder === 'name_asc') {
        return (a.name || '').localeCompare(b.name || '');
      } else if (sortOrder === 'date_desc') {
        return new Date(b.lastInteractionTimestamp || 0) - new Date(a.lastInteractionTimestamp || 0);
      } else if (sortOrder === 'orderCount_desc') {
        return b.orderCount - a.orderCount;
      }
      return 0;
    });

    setFilteredClients(results);
  }, [searchTerm, enrichedClients, sortOrder]);

  // Estatísticas calculadas dinamicamente
  const stats = useMemo(() => {
    const total = enrichedClients.length;
    const totalOrders = enrichedClients.reduce((acc, c) => acc + (c.orderCount || 0), 0);
    const active = enrichedClients.filter(c => c.status && c.status !== 'idle').length;
    const vip = enrichedClients.reduce((max, c) => (c.orderCount > (max?.orderCount || 0) ? c : max), null);
    return { total, totalOrders, active, vip };
  }, [enrichedClients]);

  const handleEdit = (client) => {
    setEditingClient({ ...client });
  };

  const handleCancelEdit = () => {
    setEditingClient(null);
  };

  const handleSaveEdit = async () => {
    if (!editingClient) return;
    try {
      const dataToUpdate = {
        name: editingClient.name,
        address: editingClient.address,
      };
      await updateCustomer(editingClient.id, dataToUpdate);
      
      // Atualiza a lista localmente para refletir a mudança imediatamente
      setEnrichedClients(prevClients => 
        prevClients.map(c => c.id === editingClient.id ? editingClient : c)
      );
      setEditingClient(null);
    } catch (e) {
      alert(`Erro ao salvar: ${e.message}`);
      console.error(e);
    }
  };

  const handleOpenChatHistory = async (client) => {
    setSelectedClientForChat(client);
    setChatHistory([]);
    setLoadingChat(true);
    try {
      const messages = await getCustomerMessages(client.id);
      setChatHistory(messages);
    } catch (e) {
      console.error("Erro ao buscar histórico de mensagens:", e);
      alert("Erro ao buscar histórico de mensagens do cliente.");
    } finally {
      setLoadingChat(false);
    }
  };

  const handleCloseChatHistory = () => {
    setSelectedClientForChat(null);
    setChatHistory([]);
  };

  const formatAddress = (address) => {
    if (!address || !address.streetAndNumber) return 'Sem endereço cadastrado';
    return `${address.streetAndNumber}, ${address.neighborhood || 'Bairro não informado'}`;
  };

  const getAvatarGradient = (name) => {
    const formattedName = name || 'Balcão';
    const hash = [...formattedName].reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colors = [
      'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
      'linear-gradient(135deg, #4E65FF 0%, #92EFFD 100%)',
      'linear-gradient(135deg, #7F00FF 0%, #E100FF 100%)',
      'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      'linear-gradient(135deg, #f12711 0%, #f5af19 100%)',
      'linear-gradient(135deg, #FF007F 0%, #7F00FF 100%)',
      'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)'
    ];
    return colors[hash % colors.length];
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.trim().split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      idle: { label: 'Inativo', color: '#8a99ad', bg: '#f1f3f5' },
      chatting: { label: 'Conversando', color: '#3b82f6', bg: '#eff6ff' },
      ordering: { label: 'Comprando', color: '#10b981', bg: '#ecfdf5' },
      awaiting_delivery: { label: 'Entregando', color: '#f59e0b', bg: '#fffbeb' },
      awaiting_pickup: { label: 'Retirada', color: '#8b5cf6', bg: '#f5f3ff' },
      awaiting_admin_mode_choice: { label: 'Menu', color: '#eab308', bg: '#fef9c3' }
    };
    const s = statusMap[status] || { label: status || 'Ativo', color: '#06b6d4', bg: '#ecfeff' };
    return (
      <span className={styles.statusBadge} style={{ color: s.color, backgroundColor: s.bg, border: `1px solid ${s.color}30` }}>
        {s.label}
      </span>
    );
  };

  if (loading) return (
    <div className={styles.loadingContainer}>
      <div className={styles.spinner}></div>
      <p className={styles.message}>Carregando painel CRM de clientes...</p>
    </div>
  );
  if (error) return (
    <div className={styles.errorContainer}>
      <div className={styles.errorIcon}>⚠️</div>
      <p className={`${styles.message} ${styles.error}`}>{error}</p>
    </div>
  );

  const renderClientRow = (client) => {
    const isEditing = editingClient && editingClient.id === client.id;
    const avatarStyle = { background: getAvatarGradient(client.name) };

    if (isEditing) {
      return (
        <tr key={client.id} className={styles.editingRow}>
          <td className={styles.clientCol}>
            <div className={styles.avatar} style={avatarStyle}>
              {getInitials(client.name)}
            </div>
            <div className={styles.clientMetaInfo}>
              <input 
                type="text" 
                value={editingClient.name} 
                onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })} 
                className={styles.editInputName}
                placeholder="Nome do cliente"
              />
              <div className={styles.statusEditWrapper}>
                {getStatusBadge(client.status)}
              </div>
            </div>
          </td>
          <td className={styles.phoneCol}>
            <span className={styles.phoneBadge}><FaPhone className={styles.metaIcon} /> {client.id}</span>
          </td>
          <td className={styles.addressCol}>
            <div className={styles.addressEditFields}>
              <input 
                type="text" 
                value={editingClient.address?.streetAndNumber || ''} 
                onChange={(e) => setEditingClient({ ...editingClient, address: { ...editingClient.address, streetAndNumber: e.target.value } })} 
                className={styles.editInputAddress}
                placeholder="Rua e Número"
              />
              <input 
                type="text" 
                value={editingClient.address?.neighborhood || ''} 
                onChange={(e) => setEditingClient({ ...editingClient, address: { ...editingClient.address, neighborhood: e.target.value } })} 
                className={styles.editInputNeighborhood}
                placeholder="Bairro"
              />
            </div>
          </td>
          <td className={styles.ordersCol}>
            <span className={styles.ordersCount}><FaShoppingCart className={styles.metaIcon} /> {client.orderCount}</span>
          </td>
          <td className={styles.interactionCol}>
            <span className={styles.interactionTime}>
              <FaClock className={styles.metaIcon} /> 
              {client.lastInteractionTimestamp ? new Date(client.lastInteractionTimestamp).toLocaleString('pt-BR') : 'N/A'}
            </span>
          </td>
          <td className={styles.actions}>
            <div className={styles.actionButtons}>
              <button onClick={handleSaveEdit} className={styles.saveBtn} title="Salvar Alterações"><FaSave /> Salvar</button>
              <button onClick={handleCancelEdit} className={styles.cancelBtn} title="Cancelar Edição"><FaTimes /> Cancelar</button>
            </div>
          </td>
        </tr>
      );
    }

    return (
      <tr key={client.id} className={styles.clientRow}>
        <td className={styles.clientCol}>
          <div className={styles.avatar} style={avatarStyle}>
            {getInitials(client.name)}
          </div>
          <div className={styles.clientMetaInfo}>
            <span className={styles.clientName}>{client.name || 'Sem Nome'}</span>
            <div className={styles.statusBadgeContainer}>
              {getStatusBadge(client.status)}
            </div>
          </div>
        </td>
        <td className={styles.phoneCol}>
          <span className={styles.phoneBadge}><FaPhone className={styles.metaIcon} /> {client.id}</span>
        </td>
        <td className={styles.addressCol}>
          <span className={styles.addressText}><FaMapMarkerAlt className={styles.metaIcon} /> {formatAddress(client.address)}</span>
        </td>
        <td className={styles.ordersCol}>
          <span className={`${styles.ordersCount} ${client.orderCount > 5 ? styles.vipPill : ''}`}>
            <FaShoppingCart className={styles.metaIcon} /> {client.orderCount} {client.orderCount > 5 ? '🔥 VIP' : ''}
          </span>
        </td>
        <td className={styles.interactionCol}>
          <span className={styles.interactionTime}>
            <FaClock className={styles.metaIcon} /> 
            {client.lastInteractionTimestamp ? new Date(client.lastInteractionTimestamp).toLocaleString('pt-BR') : 'Sem registro'}
          </span>
        </td>
        <td className={styles.actions}>
          <div className={styles.actionButtons}>
            <button onClick={() => handleOpenChatHistory(client)} className={styles.historyBtn} title="Histórico de Conversas"><FaComment className={styles.metaIcon} /> Chat</button>
            <button onClick={() => handleEdit(client)} className={styles.editBtn} title="Editar Cadastro"><FaEdit /> Editar</button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.container}>
      {/* Grid de Estatísticas de Alta Tecnologia */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
            <FaUser />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Clientes Cadastrados</span>
            <span className={styles.statValue}>{stats.total}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
            <FaShoppingCart />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Total de Vendas / Pedidos</span>
            <span className={styles.statValue}>{stats.totalOrders}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
            <FaChartLine />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Interações Ativas</span>
            <span className={styles.statValue}>{stats.active}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
            <FaUser />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Cliente VIP Principal</span>
            <span className={styles.statValue} style={{ fontSize: '1.05rem', fontWeight: 600 }}>
              {stats.vip ? `${stats.vip.name || 'Sem nome'} (${stats.vip.orderCount} ped.)` : 'Nenhum'}
            </span>
          </div>
        </div>
      </div>

      {/* Seção de Filtros e Busca */}
      <div className={styles.filterContainer}>
        <div className={styles.searchWrapper}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Buscar cliente por nome, telefone ou endereço..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.sortWrapper}>
          <FaFilter className={styles.filterIcon} />
          <span className={styles.filterLabel}>Ordenar por:</span>
          <select id="sort-order" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className={styles.sortSelect}>
            <option value="date_desc">Última Interação</option>
            <option value="orderCount_desc">Número de Pedidos</option>
            <option value="name_asc">Nome (A-Z)</option>
          </select>
        </div>
      </div>

      {/* Tabela de Clientes */}
      <div className={styles.clientList}>
        <table className={styles.clientsTable}>
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Telefone</th>
              <th>Endereço</th>
              <th>Nº de Pedidos</th>
              <th>Última Interação</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length > 0 ? (
              filteredClients.map(renderClientRow)
            ) : (
              <tr>
                <td colSpan="6" className={styles.noResults}>
                  Nenhum cliente encontrado correspondente aos filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de Histórico de Conversa */}
      {selectedClientForChat && (
        <div className={styles.modalOverlay} onClick={handleCloseChatHistory}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitleArea}>
                <div className={styles.modalAvatar} style={{ background: getAvatarGradient(selectedClientForChat.name) }}>
                  {getInitials(selectedClientForChat.name)}
                </div>
                <div>
                  <h3 className={styles.modalTitle}>{selectedClientForChat.name || 'Sem Nome'}</h3>
                  <span className={styles.modalSubtitle}>{selectedClientForChat.id}</span>
                </div>
              </div>
              <button className={styles.closeModalBtn} onClick={handleCloseChatHistory}>
                <FaTimes />
              </button>
            </div>
            
            <div className={styles.modalBody}>
              {loadingChat ? (
                <div className={styles.chatLoadingWrapper}>
                  <div className={styles.spinner}></div>
                  <p>Carregando histórico de conversas...</p>
                </div>
              ) : chatHistory.length > 0 ? (
                <div className={styles.chatMessagesArea}>
                  {chatHistory.map((msg, index) => {
                    const isAdmin = msg.role === 'model';
                    const msgDate = msg.timestamp ? new Date(msg.timestamp).toLocaleString('pt-BR') : '';
                    const bodyText = msg.parts?.[0]?.text || msg.text?.body || '';

                    return (
                      <div key={index} className={`${styles.chatBubbleWrapper} ${isAdmin ? styles.adminBubbleWrapper : styles.clientBubbleWrapper}`}>
                        <div className={`${styles.chatBubble} ${isAdmin ? styles.adminBubble : styles.clientBubble}`}>
                          <p className={styles.chatText}>{bodyText}</p>
                          <span className={styles.chatTime}>{msgDate}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.noChatMessages}>
                  <p>Nenhuma mensagem trocada com este cliente ainda.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;